import { FormatDate } from "../../../utils";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { I18n } from "react-redux-i18n";
import instance, {
  ApiError,
  ApiResponse,
  AsyncThunkConfig,
  isAxiosError,
} from "../../../api/axiosConfig";
import { Examinee } from "../../../api/examineesApi";
import {
  CreateAnonymousExamineeData,
  CreateExamineeData,
  EditExamineeDataRequest,
  ImportExamineeData,
} from "../../../components/Forms/types";
import { BackendDateFormat } from "../../../utils";
import { examineesURL } from "../../../utils/paths";
import { dispatchNotification } from "../../../utils/redux";

export const createExaminee = createAsyncThunk<
  Examinee,
  CreateExamineeData | CreateAnonymousExamineeData,
  AsyncThunkConfig
>("users/examinees/create", async (data, thunkAPI) => {
  const dataNotAnonymous = data as CreateExamineeData;

  if ((dataNotAnonymous.identificationNumber ?? "") !== "") {
    dataNotAnonymous.isIdentificationNumPesel = true;
  } else {
    dataNotAnonymous.isIdentificationNumPesel = false;
  }
  const formatedBirthDate = FormatDate(
    dataNotAnonymous.birthDate,
    BackendDateFormat
  );

  if (!dataNotAnonymous.isIdentificationNumPesel) {
    dataNotAnonymous.identificationNumber = undefined;
  }
  try {
    const response = await instance.post<ApiResponse<Examinee>>(examineesURL, {
      ...data,
      birthDate: formatedBirthDate,
      isIdentificationNumPesel: dataNotAnonymous.isIdentificationNumPesel,
      identificationNumber: dataNotAnonymous.identificationNumber,
    });

    dispatchNotification("success", I18n.t("Snackbars.createExamineeSuccess"));

    return response.data.results;
  } catch (err) {
    if (isAxiosError(err) && err.response != null) {
      const extractedError = err.response.data as ApiError;
      const { message, details } = extractedError;

      // Sprawdź czy to błąd walidacji z konkretnymi polami
      if (details && typeof details === "object") {
        // Sprawdź czy to błąd walidacji pola birth_date
        if ((details as any).birth_date) {
          dispatchNotification(
            "error",
            I18n.t("Forms.snackbar.required"),
            true
          );
        } else if ((details as any).sex) {
          dispatchNotification(
            "error",
            I18n.t("Forms.snackbar.required"),
            true
          );
        } else if ((details as any).directory) {
          dispatchNotification(
            "error",
            I18n.t("Forms.snackbar.required"),
            true
          );
        } else if (
          (details as any)?.message ===
          "Examinee_with_the_given_id_exists_in_this_psychological_lab"
        ) {
          dispatchNotification(
            "error",
            "Examinee_with_the_given_id_exists_in_this_psychological_lab",
            true
          );
        } else {
          dispatchNotification("error", message, true);
        }
      } else if (
        (details as any)?.message ===
        "Examinee_with_the_given_id_exists_in_this_psychological_lab"
      ) {
        dispatchNotification(
          "error",
          "Examinee_with_the_given_id_exists_in_this_psychological_lab",
          true
        );
      } else {
        dispatchNotification("error", message, true);
      }

      return thunkAPI.rejectWithValue(extractedError);
    }

    dispatchNotification("error");
    throw err;
  }
});

export const editExaminee = createAsyncThunk<
  Examinee,
  EditExamineeDataRequest,
  AsyncThunkConfig
>("users/examinees/update", async (data, thunkAPI) => {
  const formatedBirthDate = FormatDate(data.birthDate, BackendDateFormat);

  try {
    const response = await instance.patch<ApiResponse<Examinee>>(
      `${examineesURL}${data.id}/`,
      { ...data, birthDate: formatedBirthDate }
    );

    dispatchNotification("success", I18n.t("Snackbars.editExamineeSuccess"));

    return response.data.results;
  } catch (err) {
    if (isAxiosError(err) && err.response != null) {
      const extractedError = err.response.data as ApiError;
      const { message, details } = extractedError;

      // Sprawdź czy to błąd walidacji z konkretnymi polami
      if (details && typeof details === "object") {
        // Sprawdź czy to błąd walidacji pola birth_date
        if ((details as any).birth_date) {
          dispatchNotification(
            "error",
            I18n.t("Forms.snackbar.required"),
            true
          );
        } else if ((details as any).sex) {
          dispatchNotification(
            "error",
            I18n.t("Forms.snackbar.required"),
            true
          );
        } else if ((details as any).directory) {
          dispatchNotification(
            "error",
            I18n.t("Forms.snackbar.required"),
            true
          );
        } else if (
          (details as any)?.message?.[0] ===
          "Examinee_with_the_given_id_exists_in_this_psychological_lab"
        ) {
          dispatchNotification(
            "error",
            "Examinee_with_the_given_id_exists_in_this_psychological_lab",
            true
          );
        } else {
          dispatchNotification("error", message, true);
        }
      } else if (
        (details as any)?.message?.[0] ===
        "Examinee_with_the_given_id_exists_in_this_psychological_lab"
      ) {
        dispatchNotification(
          "error",
          "Examinee_with_the_given_id_exists_in_this_psychological_lab",
          true
        );
      } else {
        dispatchNotification("error", message, true);
      }

      return thunkAPI.rejectWithValue(extractedError);
    }

    dispatchNotification("error");
    throw err;
  }
});

export const importExaminees = createAsyncThunk<
  void,
  ImportExamineeData[],
  AsyncThunkConfig
>("users/examinees/import", async (data, thunkAPI) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const response = await instance.post<{ message: string }>(
      `${examineesURL}import/`,
      data
    );
  } catch (err) {
    if (isAxiosError(err) && err.response != null) {
      const extractedError = err.response.data as ApiError;

      if (extractedError.results) {
        const { message } = extractedError.results;

        if (
          message ===
          "Examinee_with_the_given_id_exists_in_this_psychological_lab"
        ) {
          dispatchNotification(
            "error",
            "Examinee_with_the_given_id_exists_in_this_psychological_lab",
            true
          );
        } else if (message === "Generated_ID_conflict") {
          dispatchNotification("error", "Generated_ID_conflict", true);
        } else {
          dispatchNotification("error", message, true);
        }
      }

      return thunkAPI.rejectWithValue(extractedError);
    }

    dispatchNotification("error");
    throw err;
  }
});